// PR-31: Chat tab — message list + composer.
// PR-32: full DiscussionPane parity.
//
// Renders all four desktop ChatMessage kinds: user / assistant / thinking /
// tool_call. Wikilinks resolve via CardBodyMarkdown (shared with desktop) and
// route through onJumpToCard for cross-tab navigation. Tool calls render as
// expandable chips; clicking a resolvable propose_card / write_card jumps to
// the target card. Thinking bubbles default to expanded while streaming and
// auto-collapse once `done=true`. Long-press a message → action sheet with
// Copy. Pinned gap banner sits above the stream when the active thread is
// "discussing" a gap.
//
// Things still owned by parent (PR-32 contract):
//   - markdown rendering uses `knownIds` from MobileShell so unresolved
//     wikilinks stay literal text (no broken jumps)
//   - tool-call → card resolution lives here (mirror of desktop helper)
//   - pinned gap state is per-thread in MobileShell; banner just renders +
//     fires callbacks back up

import { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition'
import { CardBodyMarkdown } from '../cards/CardBodyMarkdown'
import type { Card, ModelChoices, ThreadUsage } from '../../api'
import { MobileTokenUsageChip } from '../TokenUsageChip'
import type { MobileChatMessage } from './chat_types'

interface MobileChatProps {
  threadTitle: string | null | undefined
  hasActiveThread: boolean
  messages: MobileChatMessage[]
  busy: boolean
  disabled?: boolean
  onSend: (text: string) => void | Promise<void>

  /** All cards in the active project, used for wikilink resolution and
   *  tool-call → card lookup. Empty array is fine; we just don't resolve. */
  cards: Card[]
  /** Cross-tab navigation: jump to a card in the Cards tab and open its
   *  fullscreen peek. Wired in MobileShell. */
  onJumpToCard: (cardId: string) => void

  /** Pinned gap (gap card the user clicked Discuss on). When set, banner
   *  shows above the message stream. null when no pin. */
  pinnedGap: Card | null
  onResolveGap: () => void
  onApprovePinnedGap: (cardId: string) => void
  onRejectPinnedGap: (cardId: string) => void

  /** PR-58 mobile: tier picker. `modelChoices` is null while the
   *  initial fetch is in flight — chip stays hidden in that case.
   *  `threadModel` is the active thread's stored tier id (or raw model
   *  id for legacy rows); null means "use server default". `onChangeTier`
   *  applies the choice and persists it for future new threads. */
  modelChoices: ModelChoices | null
  threadModel: string | null
  onChangeTier: (tierId: string | null) => void | Promise<void>

  /** Per-thread context-window snapshot. Drives the mobile context chip
   *  next to the tier picker. Null while the initial getThreadUsage is in
   *  flight or before any turn has run on this thread. */
  usage?: ThreadUsage | null
}

function friendlySpeechError(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone access denied. Enable it in browser settings.'
    case 'audio-capture':
      return 'Microphone unavailable. Check that no other app is using it.'
    case 'network':
      return 'Voice service unreachable. Check your connection.'
    case 'aborted':
      return 'Voice input was interrupted.'
    default:
      return `Voice input failed: ${code}`
  }
}

export function MobileChat({
  threadTitle,
  hasActiveThread,
  messages,
  busy,
  disabled,
  onSend,
  cards,
  onJumpToCard,
  pinnedGap,
  onResolveGap,
  onApprovePinnedGap,
  onRejectPinnedGap,
  modelChoices,
  threadModel,
  onChangeTier,
  usage,
}: MobileChatProps) {
  const [tierSheetOpen, setTierSheetOpen] = useState(false)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [errorBanner, setErrorBanner] = useState<string | null>(null)
  const [actionSheetIdx, setActionSheetIdx] = useState<number | null>(null)

  // PR-37: contenteditable div instead of <textarea>. iOS Safari shows a
  // cursor-navigation accessory bar (`^ v ✓`) above the keyboard for any
  // textarea — it's a system feature and CSS can't hide it. ChatGPT works
  // around it by using a contenteditable div, which iOS treats more like a
  // single-line input (no nav arrows). State here is the source of truth;
  // the editor's textContent is updated imperatively only when the value
  // changes from outside the user's typing (voice append, send-clear).
  const editorRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const speech = useSpeechRecognition()

  // Speech-finalized chunks are appended to whatever's currently in the
  // editor. We update both the DOM (so the cursor stays at the end of the
  // appended text) and the React state.
  useEffect(() => {
    if (!speech.final) return
    const editor = editorRef.current
    const existing = editor?.textContent ?? ''
    const next = (existing ? existing + ' ' : '') + speech.final
    if (editor) {
      editor.textContent = next
      placeCursorAtEnd(editor)
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setText(next)
    speech.reset()
  }, [speech.final, speech])

  useEffect(() => {
    if (!speech.error) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setErrorBanner(friendlySpeechError(speech.error))
    const t = setTimeout(() => setErrorBanner(null), 4000)
    return () => clearTimeout(t)
  }, [speech.error])

  // Sticky-bottom autoscroll. If the user scrolled up to read history,
  // don't yank them back when new content arrives.
  const stickyBottomRef = useRef(true)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop
      stickyBottomRef.current = distanceFromBottom < 64
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (!stickyBottomRef.current) return
    el.scrollTop = el.scrollHeight
  }, [messages, busy])

  // knownIds memo for wikilink resolution. ReadonlySet to match
  // CardBodyMarkdown's contract; identity is stable across renders so the
  // markdown component memoizes well.
  const knownIds = useMemo(
    () => new Set(cards.map((c) => c.card_id)),
    [cards],
  )

  const onMicTap = () => {
    if (speech.recording) speech.stop()
    else speech.start()
  }

  const submit = async () => {
    if (speech.recording) speech.stop()
    const trimmed = text.trim()
    if (!trimmed || sending || disabled) return
    setSending(true)
    try {
      // Optimistic clear — same UX as PR-31 textarea.
      if (editorRef.current) editorRef.current.textContent = ''
      setText('')
      await onSend(trimmed)
    } finally {
      setSending(false)
    }
  }

  const onEditorInput = () => {
    setText(editorRef.current?.textContent ?? '')
  }

  const onEditorPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    // contenteditable accepts rich HTML by default; force plain text so
    // pasted content can't smuggle in formatting / links / images.
    e.preventDefault()
    const plain = e.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, plain)
  }

  // Long-press detection on a bubble. Pointer-based so dev-tools mobile
  // emulator works alongside touch.
  //
  // Perf: `startLongPress` and `cancelLongPress` MUST stay reference-stable
  // (no deps) so the per-bubble `React.memo` shells don't re-render every
  // time the parent re-renders. Bubbles invoke `startLongPress(idx)`
  // themselves rather than receiving a per-row closure.
  const longPressTimer = useRef<number | null>(null)
  const longPressFiredRef = useRef(false)
  const startLongPress = useCallback((idx: number) => {
    longPressFiredRef.current = false
    if (longPressTimer.current) window.clearTimeout(longPressTimer.current)
    longPressTimer.current = window.setTimeout(() => {
      longPressFiredRef.current = true
      setActionSheetIdx(idx)
    }, 450)
  }, [])
  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      window.clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  const actionSheetMessage =
    actionSheetIdx !== null ? messages[actionSheetIdx] ?? null : null
  const actionSheetText = actionSheetMessage
    ? actionSheetMessage.kind === 'user' ||
      actionSheetMessage.kind === 'assistant' ||
      actionSheetMessage.kind === 'thinking'
      ? actionSheetMessage.text
      : actionSheetMessage.kind === 'tool_call'
        ? actionSheetMessage.input
        : ''
    : ''

  return (
    <section className="mobile-chat">
      {pinnedGap && (
        <PinnedGapBanner
          gap={pinnedGap}
          knownIds={knownIds}
          onJumpToCard={onJumpToCard}
          onResolve={onResolveGap}
          onApprove={() => onApprovePinnedGap(pinnedGap.card_id)}
          onReject={() => onRejectPinnedGap(pinnedGap.card_id)}
        />
      )}

      {(modelChoices && modelChoices.tiers.length > 1) || usage ? (
        <ModelChipRow
          modelChoices={modelChoices}
          threadModel={threadModel}
          onOpen={() => setTierSheetOpen(true)}
          usage={usage ?? null}
        />
      ) : null}

      <div className="mobile-chat__scroll" ref={scrollRef}>
        {!hasActiveThread && messages.length === 0 ? (
          <div className="mobile-chat__empty">
            <div className="mobile-chat__empty-title">No conversation yet</div>
            <div className="mobile-chat__empty-hint">
              Type below to start — a new thread will be created automatically.
            </div>
          </div>
        ) : (
          <>
            {hasActiveThread && (
              <div className="mobile-chat__thread-label mono">
                {threadTitle?.trim() ? threadTitle : 'untitled'}
              </div>
            )}
            <CardsContext.Provider value={cards}>
              {messages.map((m, i) => (
                <BubbleRow
                  key={i}
                  idx={i}
                  msg={m}
                  knownIds={knownIds}
                  onJumpToCard={onJumpToCard}
                  onLongPress={startLongPress}
                  onLongPressEnd={cancelLongPress}
                />
              ))}
            </CardsContext.Provider>
            {busy && messages.length > 0 && messages[messages.length - 1]?.kind !== 'thinking' &&
              !(messages[messages.length - 1]?.kind === 'assistant' &&
                (messages[messages.length - 1] as { streaming?: boolean })?.streaming) && (
              <div className="mobile-chat__thinking-line" aria-live="polite">
                <span className="mobile-chat__thinking-glyph">✦</span> thinking…
              </div>
            )}
          </>
        )}
      </div>

      <div className="mobile-chat__composer">
        {speech.recording && (
          <div className="mobile-chat__interim mono" aria-live="polite">
            {speech.interim || 'Listening…'}
          </div>
        )}
        <div className="mobile-chat__input-row">
          <div className="mobile-chat__editor-wrap">
            <div
              ref={editorRef}
              className="mobile-chat__editor"
              role="textbox"
              // PR-38: deliberately NO aria-multiline. iOS Safari adds
              // cursor up/down arrows to its keyboard accessory bar when
              // a focused element advertises multi-line.
              aria-label={hasActiveThread ? 'Message' : 'Start a new conversation'}
              // PR-39: hint iOS soft keyboard's Return key to show "Send".
              // Combined with the keydown handler below, plain Enter
              // submits — matches ChatGPT mobile / Telegram / WhatsApp.
              enterKeyHint="send"
              contentEditable={!disabled}
              suppressContentEditableWarning
              onInput={onEditorInput}
              onPaste={onEditorPaste}
              onKeyDown={(e) => {
                // Cmd/Ctrl+Enter still submits (desktop convention).
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault()
                  void submit()
                  return
                }
                // PR-39: plain Enter submits on mobile. Shift+Enter
                // (Bluetooth keyboard / power users) inserts a newline.
                // The on-screen send button is still there but iOS
                // sometimes pushes it off-screen on focus zoom — Return
                // is now a guaranteed escape hatch.
                if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey) {
                  e.preventDefault()
                  void submit()
                }
              }}
            />
            {!text && (
              <div className="mobile-chat__placeholder" aria-hidden>
                {hasActiveThread ? 'Message…' : 'Start a new conversation…'}
              </div>
            )}
          </div>
          {speech.supported && (
            <button
              type="button"
              className={
                'mobile-chat__mic' +
                (speech.recording ? ' mobile-chat__mic--recording' : '')
              }
              onClick={onMicTap}
              aria-label={speech.recording ? 'Stop recording' : 'Start voice input'}
              aria-pressed={speech.recording}
              disabled={disabled || sending}
            >
              {speech.recording ? '■' : '🎙'}
            </button>
          )}
          <button
            type="button"
            className="mobile-chat__send"
            onClick={submit}
            disabled={!text.trim() || sending || disabled}
            aria-label="Send message"
          >
            ↑
          </button>
        </div>
      </div>

      {errorBanner && (
        <div
          className="mobile-toast mobile-toast--error"
          role="alert"
          onClick={() => setErrorBanner(null)}
        >
          {errorBanner}
        </div>
      )}

      {actionSheetMessage && (
        <div
          className="mobile-action-sheet-backdrop"
          onClick={() => setActionSheetIdx(null)}
        >
          <div
            className="mobile-action-sheet"
            onClick={(e) => e.stopPropagation()}
            role="menu"
          >
            <div className="mobile-action-sheet__title">
              {actionSheetMessage.kind === 'tool_call'
                ? `Tool: ${actionSheetMessage.tool}`
                : actionSheetMessage.kind === 'thinking'
                  ? 'Reasoning'
                  : actionSheetMessage.kind === 'user'
                    ? 'Your message'
                    : 'Assistant message'}
            </div>
            <button
              type="button"
              className="mobile-action-sheet__row"
              onClick={async () => {
                try {
                  await navigator.clipboard?.writeText(actionSheetText)
                } catch {
                  /* clipboard may be unavailable in the WeChat in-app
                     browser; fail silently rather than throwing */
                }
                setActionSheetIdx(null)
              }}
            >
              Copy text
            </button>
            <button
              type="button"
              className="mobile-action-sheet__row mobile-action-sheet__row--cancel"
              onClick={() => setActionSheetIdx(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {tierSheetOpen && modelChoices && (
        <TierPickerSheet
          modelChoices={modelChoices}
          threadModel={threadModel}
          onPick={(tierId) => {
            setTierSheetOpen(false)
            void onChangeTier(tierId)
          }}
          onClose={() => setTierSheetOpen(false)}
        />
      )}
    </section>
  )
}

// ── PR-58 mobile: model picker ──────────────────────────────────────
//
// Slim header chip showing current tier + tap target. Sits between the
// pinned-gap banner and the message stream, always visible when more
// than one tier is configured. Tap → bottom sheet picker.

function ModelChipRow({
  modelChoices,
  threadModel,
  onOpen,
  usage,
}: {
  modelChoices: ModelChoices | null
  threadModel: string | null
  onOpen: () => void
  /** PR-58 desktop port: context-window chip rendered to the right of
   *  the tier picker. Hidden when usage hasn't hydrated yet. */
  usage: ThreadUsage | null
}) {
  // Resolve the tier the active thread is using. Stored value can be
  // either a tier id ("flash-1m") or a raw model id ("glm-5-turbo");
  // the server's tier_by_model_id reverse-lookup mirrors this. When
  // nothing is stored, fall back to the picker default (server has
  // already aligned this with DRAFTLIZE_MODEL).
  const showTierPicker =
    modelChoices !== null && modelChoices.tiers.length > 1
  const tier = modelChoices
    ? (modelChoices.tiers.find(
        (t) => t.id === (threadModel ?? modelChoices.default_tier),
      ) ??
        modelChoices.tiers.find((t) => t.id === modelChoices.default_tier) ??
        modelChoices.tiers[0])
    : null
  const label = tier
    ? tier.display_name
    : threadModel ?? modelChoices?.default_tier ?? ''
  return (
    <div className="mobile-chat__model-row">
      {showTierPicker && (
        <button
          type="button"
          className="mobile-chat__model-chip mono"
          onClick={onOpen}
          aria-label="Change model"
        >
          <span>{label}</span>
          <span aria-hidden style={{ marginLeft: 4, fontSize: 9 }}>▾</span>
        </button>
      )}
      {usage && <MobileTokenUsageChip usage={usage} />}
    </div>
  )
}

function TierPickerSheet({
  modelChoices,
  threadModel,
  onPick,
  onClose,
}: {
  modelChoices: ModelChoices
  threadModel: string | null
  onPick: (tierId: string | null) => void
  onClose: () => void
}) {
  const effectiveTierId = threadModel ?? modelChoices.default_tier
  return (
    <div className="mobile-action-sheet-backdrop" onClick={onClose}>
      <div
        className="mobile-action-sheet"
        onClick={(e) => e.stopPropagation()}
        role="menu"
      >
        <div className="mobile-action-sheet__title">Model</div>
        {modelChoices.tiers.map((tier) => {
          const selected = effectiveTierId === tier.id
          const isDefault = tier.id === modelChoices.default_tier
          // Picking the server default writes NULL to threads.model
          // (clears the override); anything else stores the tier id
          // explicitly. Mirrors desktop ModelChip behaviour.
          const value = isDefault ? null : tier.id
          return (
            <button
              key={tier.id}
              type="button"
              className="mobile-action-sheet__row"
              onClick={() => onPick(value)}
            >
              <span>
                {selected ? '✓ ' : '   '}
                {tier.display_name}
                {isDefault && (
                  <span className="mobile-chat__model-default-tag mono">
                    {' '}DEFAULT
                  </span>
                )}
              </span>
              <span className="mobile-topbar__action-hint mono">
                {tier.description}
              </span>
            </button>
          )
        })}
        <button
          type="button"
          className="mobile-action-sheet__row mobile-action-sheet__row--cancel"
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

// ── Bubble row dispatcher ───────────────────────────────────────────
//
// One memo'd shell per bubble, dispatched by `msg.kind` to a typed
// subcomponent. The shell is the React.memo boundary that lets history
// bubbles skip re-rendering as new tokens stream into the LAST bubble:
// when MobileShell appends a streaming delta, the historical messages'
// references stay identical, so each `BubbleRow`'s shallow prop compare
// passes and React skips its subtree entirely.
//
// `onLongPress` and `onLongPressEnd` are the same useCallback identities
// from MobileChat for the lifetime of the page; `idx` only changes when
// the row's position changes (append-only stream → never for existing
// rows). Combined, every prop is reference-stable across token deltas.

interface BubbleRowProps {
  idx: number
  msg: MobileChatMessage
  knownIds: ReadonlySet<string>
  onJumpToCard: (cardId: string) => void
  onLongPress: (idx: number) => void
  onLongPressEnd: () => void
}

const BubbleRow = memo(function BubbleRow({
  idx,
  msg,
  knownIds,
  onJumpToCard,
  onLongPress,
  onLongPressEnd,
}: BubbleRowProps) {
  // Stable handlers per bubble: only depend on idx + the two long-press
  // callbacks (all reference-stable). Recomputing this object on the rare
  // re-render is fine; the point is that the parent re-rendering with
  // unchanged props is now free.
  const handlers = useMemo(
    () => ({
      onPointerDown: () => onLongPress(idx),
      onPointerUp: onLongPressEnd,
      onPointerLeave: onLongPressEnd,
      onPointerCancel: onLongPressEnd,
      onTouchEnd: onLongPressEnd,
      onTouchMove: onLongPressEnd,
      onTouchCancel: onLongPressEnd,
    }),
    [idx, onLongPress, onLongPressEnd],
  )

  switch (msg.kind) {
    case 'user':
      return <UserBubble text={msg.text} handlers={handlers} />
    case 'assistant':
      return (
        <AssistantBubble
          text={msg.text}
          streaming={msg.streaming === true}
          truncated={msg.truncated === true}
          knownIds={knownIds}
          onJumpToCard={onJumpToCard}
          handlers={handlers}
        />
      )
    case 'thinking':
      return (
        <ThinkingBubble
          text={msg.text}
          done={msg.done}
          handlers={handlers}
        />
      )
    case 'tool_call':
      return (
        <ToolCallChip
          tool={msg.tool}
          input={msg.input}
          onJumpToCard={onJumpToCard}
          handlers={handlers}
        />
      )
  }
})

// ── User bubble ────────────────────────────────────────────────────
type PressHandlers = {
  onPointerDown: () => void
  onPointerUp: () => void
  onPointerLeave: () => void
  onPointerCancel: () => void
  onTouchEnd: () => void
  onTouchMove: () => void
  onTouchCancel: () => void
}

const UserBubble = memo(function UserBubble({
  text,
  handlers,
}: {
  text: string
  handlers: PressHandlers
}) {
  return (
    <div
      className="mobile-chat__bubble mobile-chat__bubble--user"
      {...handlers}
    >
      <div className="mobile-chat__bubble-text">{text}</div>
    </div>
  )
})

// ── Assistant bubble ───────────────────────────────────────────────
//
// Streaming path renders the buffer as plain text so we don't re-parse
// markdown on every token (the dominant CPU cost on long replies). On
// completion the bubble swaps in the full markdown render — that's a
// one-time parse per reply rather than O(tokens).
const AssistantBubble = memo(function AssistantBubble({
  text,
  streaming,
  truncated,
  knownIds,
  onJumpToCard,
  handlers,
}: {
  text: string
  streaming: boolean
  truncated: boolean
  knownIds: ReadonlySet<string>
  onJumpToCard: (cardId: string) => void
  handlers: PressHandlers
}) {
  return (
    <div
      className="mobile-chat__bubble mobile-chat__bubble--assistant"
      {...handlers}
    >
      <div className="mobile-chat__bubble-text md-chat">
        {streaming ? (
          <>
            <span className="mobile-chat__streaming-text">{text || ' '}</span>
            <span className="mobile-chat__caret">|</span>
          </>
        ) : (
          <CardBodyMarkdown
            body={text || ' '}
            knownIds={knownIds}
            onJumpToCard={onJumpToCard}
          />
        )}
      </div>
      {truncated && (
        <div className="mobile-chat__truncated">
          ⚠ TRUNCATED · max_tokens reached — response cut off
        </div>
      )}
    </div>
  )
})

// ── Thinking bubble (collapsible) ───────────────────────────────────
const ThinkingBubble = memo(function ThinkingBubble({
  text,
  done,
  handlers,
}: {
  text: string
  done: boolean
  handlers: PressHandlers
}) {
  // Default expanded while streaming, collapsed once done. User can click
  // to toggle once it's done.
  const [expanded, setExpanded] = useState(!done)
  const wasDoneRef = useRef(done)
  useEffect(() => {
    if (!wasDoneRef.current && done) {
      setExpanded(false)
    }
    wasDoneRef.current = done
  }, [done])

  const charCount = text.length
  const label = done
    ? `${expanded ? '▾' : '▸'} reasoning${charCount > 0 ? ` (${charCount} chars)` : ''}`
    : '✦ reasoning'

  return (
    <div
      className={
        'mobile-chat__thinking' +
        (expanded ? ' mobile-chat__thinking--expanded' : '')
      }
      {...handlers}
    >
      <button
        type="button"
        className="mobile-chat__thinking-toggle"
        onClick={() => {
          if (done) setExpanded((v) => !v)
        }}
        aria-expanded={expanded}
        aria-label="Toggle reasoning"
      >
        {label}
        {!done && <span className="mobile-chat__caret">|</span>}
      </button>
      {expanded && text && (
        <div className="mobile-chat__thinking-text">{text}</div>
      )}
    </div>
  )
})

// ── Tool call chip (expandable + clickable to jump) ────────────────
//
// `cards` arrives via context so the rest of the bubbles aren't dragged
// into a re-render every time the cards list refreshes from SSE.
const CardsContext = createContext<Card[]>([])

const ToolCallChip = memo(function ToolCallChip({
  tool,
  input,
  onJumpToCard,
  handlers,
}: {
  tool: string
  input: string
  onJumpToCard: (cardId: string) => void
  handlers: PressHandlers
}) {
  const cards = useContext(CardsContext)
  const [expanded, setExpanded] = useState(false)
  const summary = useMemo(
    () => summarizeToolCall(tool, input),
    [tool, input],
  )
  const resolvedId = useMemo(
    () => resolveCardForToolCall(tool, input, cards),
    [tool, input, cards],
  )

  const onTap = () => {
    if (resolvedId) onJumpToCard(resolvedId)
    else setExpanded((v) => !v)
  }

  return (
    <div className="mobile-chat__toolcall" {...handlers}>
      <button
        type="button"
        className="mobile-chat__toolcall-row"
        onClick={onTap}
      >
        <span className="mobile-chat__toolcall-glyph">▸</span>
        <span className="mobile-chat__toolcall-tool mono">{tool}</span>
        <span className="mobile-chat__toolcall-summary">{summary}</span>
        {!resolvedId && (
          <span className="mobile-chat__toolcall-chevron" aria-hidden>
            {expanded ? '▴' : '▾'}
          </span>
        )}
      </button>
      {expanded && !resolvedId && (
        <pre className="mobile-chat__toolcall-input">{prettyJson(input)}</pre>
      )}
    </div>
  )
})

// ── Pinned gap banner ──────────────────────────────────────────────
function PinnedGapBanner({
  gap,
  knownIds,
  onJumpToCard,
  onResolve,
  onApprove,
  onReject,
}: {
  gap: Card
  knownIds: ReadonlySet<string>
  onJumpToCard: (cardId: string) => void
  onResolve: () => void
  onApprove: () => void
  onReject: () => void
}) {
  const proposedKind =
    typeof gap.extra?.['proposed_kind'] === 'string'
      ? (gap.extra['proposed_kind'] as string)
      : null
  const ambiguity =
    typeof gap.extra?.['ambiguity_reason'] === 'string'
      ? (gap.extra['ambiguity_reason'] as string)
      : null
  const title =
    gap.title?.trim() ||
    gap.body.split('\n').find((l) => l.trim().length > 0)?.trim() ||
    '(untitled gap)'

  return (
    <div className="mobile-chat__pinned-gap">
      <div className="mobile-chat__pinned-gap-head">
        <span className="mobile-chat__pinned-gap-tag mono">
          ✦ DISCUSSING {gap.card_id}
        </span>
        {proposedKind && (
          <span className="mobile-chat__pinned-gap-kind mono">
            → {proposedKind}
          </span>
        )}
      </div>
      <button
        type="button"
        className="mobile-chat__pinned-gap-title"
        onClick={() => onJumpToCard(gap.card_id)}
        title="Open gap card"
      >
        <CardBodyMarkdown body={title} knownIds={knownIds} inline />
      </button>
      {ambiguity && (
        <div className="mobile-chat__pinned-gap-why">{ambiguity}</div>
      )}
      <div className="mobile-chat__pinned-gap-actions">
        <button
          type="button"
          className="mobile-chat__pinned-gap-btn mobile-chat__pinned-gap-btn--resolve"
          onClick={onResolve}
        >
          Resolve
        </button>
        <button
          type="button"
          className="mobile-chat__pinned-gap-btn mobile-chat__pinned-gap-btn--approve"
          onClick={onApprove}
        >
          Approve
        </button>
        <button
          type="button"
          className="mobile-chat__pinned-gap-btn mobile-chat__pinned-gap-btn--reject"
          onClick={onReject}
        >
          Reject
        </button>
      </div>
    </div>
  )
}

// ── Helpers ────────────────────────────────────────────────────────
// Mirror of the desktop helpers in DiscussionPane.tsx. Duplicated rather
// than exported from the desktop file so this PR doesn't churn the desktop
// component's surface; if the duplication becomes a maintenance hotspot
// later we can lift to a shared module.

function resolveCardForToolCall(
  tool: string,
  input: string,
  cards: Card[],
): string | null {
  if (!cards || cards.length === 0) return null
  let parsed: Record<string, unknown>
  try {
    parsed = JSON.parse(input) as Record<string, unknown>
  } catch {
    return null
  }
  const str = (k: string): string | null => {
    const v = parsed[k]
    return typeof v === 'string' ? v : null
  }
  const knownId = str('id')
  if (knownId && cards.some((c) => c.card_id === knownId)) {
    return knownId
  }
  if (tool === 'read_card' || tool === 'delete_card') return null
  if (tool !== 'propose_card' && tool !== 'write_card') return null
  const wantTitle = str('title')?.trim() ?? ''
  if (!wantTitle) return null
  const wantKind = tool === 'propose_card' ? str('proposed_kind') : str('kind')

  const titleMatches = cards.filter((c) => (c.title ?? '').trim() === wantTitle)
  if (titleMatches.length === 0) return null

  const sorted = [...titleMatches].sort((a, b) => mtimeDesc(a, b))
  if (tool === 'propose_card' && wantKind) {
    const gap = sorted.find(
      (c) =>
        c.kind === 'gap' &&
        typeof c.extra?.['proposed_kind'] === 'string' &&
        (c.extra['proposed_kind'] as string) === wantKind,
    )
    if (gap) return gap.card_id
    const promoted = sorted.find((c) => c.kind === wantKind)
    if (promoted) return promoted.card_id
  } else if (tool === 'write_card' && wantKind) {
    const k = sorted.find((c) => c.kind === wantKind)
    if (k) return k.card_id
  }
  return sorted[0].card_id
}

function mtimeDesc(a: Card, b: Card): number {
  const at = a.mtime || a.updated_at || a.created_at || ''
  const bt = b.mtime || b.updated_at || b.created_at || ''
  return bt.localeCompare(at)
}

function summarizeToolCall(tool: string, input: string): string {
  let parsed: Record<string, unknown> = {}
  try {
    parsed = JSON.parse(input) as Record<string, unknown>
  } catch {
    return ` · ${truncate(input, 60)}`
  }
  const str = (k: string): string | null => {
    const v = parsed[k]
    return typeof v === 'string' ? v : null
  }
  const arr = (k: string): string[] | null => {
    const v = parsed[k]
    return Array.isArray(v)
      ? v.filter((x): x is string => typeof x === 'string')
      : null
  }
  const trimTitle = (s: string | null) =>
    s ? truncate(s.replace(/\n/g, ' '), 50) : ''

  switch (tool) {
    case 'propose_card': {
      const kind = str('proposed_kind') ?? '?'
      const title = trimTitle(str('title')) || trimTitle(str('body'))
      const blocks = arr('blocks')
      const blocksStr =
        blocks && blocks.length > 0 ? ` · blocks=${blocks.join(',')}` : ''
      return ` → ${kind} · "${title}"${blocksStr}`
    }
    case 'write_card': {
      const kind = str('kind') ?? '?'
      const id = str('id')
      const title = trimTitle(str('title')) || trimTitle(str('body'))
      return ` ${id ? `${id} ` : ''}(${kind}) "${title}"`
    }
    case 'read_card':
    case 'delete_card': {
      const id = str('id') ?? '?'
      return ` ${id}`
    }
    case 'web_search': {
      const q = str('query') ?? ''
      return ` "${truncate(q, 40)}"`
    }
    default: {
      const firstStr = Object.entries(parsed).find(
        ([, v]) => typeof v === 'string',
      )
      if (firstStr) {
        return ` · ${firstStr[0]}=${truncate(String(firstStr[1]), 40)}`
      }
      return ''
    }
  }
}

function prettyJson(input: string): string {
  try {
    return JSON.stringify(JSON.parse(input), null, 2)
  } catch {
    return input
  }
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s
  return s.slice(0, Math.max(0, n - 1)) + '…'
}

// Move the cursor to the end of a contenteditable element. Used after a
// programmatic content update (voice append) so the user can continue
// typing where the new text ends instead of where the caret was before.
function placeCursorAtEnd(el: HTMLElement) {
  const range = document.createRange()
  range.selectNodeContents(el)
  range.collapse(false)
  const sel = window.getSelection()
  if (!sel) return
  sel.removeAllRanges()
  sel.addRange(range)
}
